import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, Paper, useMediaQuery, useTheme, Card, CardContent } from "@mui/material";
import { MdAttachEmail, MdOutlineDateRange } from "react-icons/md";
import { FaFileInvoice, FaChartPie, FaRegClock } from "react-icons/fa6";
import { PiDatabaseFill } from "react-icons/pi";
import { get_user_plan } from "../Api service/APIvariables";

const UserDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [stats, setStats] = useState({
    planName: "Free",
    cardsUsed: 0,
    cardsLimit: 0,
    emailsUsed: 0,
    emailsLimit: 0,
    totalCards: 0,
    totalEmails: 0,
    planExpiry: null
  });

  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const user_id = localStorage.getItem("user_id");
        const token = localStorage.getItem("auth_token");
        if (!user_id || !token) return;

        const formData = new FormData();
        formData.append("user_id", user_id);

        const res = await fetch(get_user_plan, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        const data = await res.json();
        if (!data.error) {
          setStats({
            planName: data.plan_name || "Free",
            cardsUsed: data.cards_today || 0,
            cardsLimit: data.card_limit || 0,
            emailsUsed: data.emails_today || 0,
            emailsLimit: data.email_limit || 0,
            totalCards: data.total_cards || 0,
            totalEmails: data.total_emails || 0,
            planExpiry: data.plan_expiry_date || null
          });
        }
      } catch (err) {}
    };
    fetchPlan();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getCardsBalance = () => stats.cardsLimit === 999999 ? "Unlimited" : `${Math.max(0, stats.cardsLimit - stats.cardsUsed)} / ${stats.cardsLimit}`;
  const getEmailsBalance = () => stats.emailsLimit === 999999 ? "Unlimited" : `${Math.max(0, stats.emailsLimit - stats.emailsUsed)} / ${stats.emailsLimit}`;

  // Reusable card for the clean QuickCard vibe
  const MetricCard = ({ title, value, icon, color }) => (
    <Card elevation={0} sx={{ borderRadius: "12px", border: "1px solid #e0e0e0", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3, '&:last-child': { pb: 3 } }}>
        <Box sx={{ p: 1.5, borderRadius: "8px", backgroundColor: `${color}15`, color: color, mr: 2, display: 'flex' }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="body2" color="textSecondary" fontWeight="600" sx={{ mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="h5" fontWeight="bold" color="#333">
            {value}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: isMobile ? 1 : 2, width: "100%", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Header section matching QuickCard profile/admin styling */}
      <Box sx={{ mb: 4, display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center", backgroundColor: "white", p: 3, borderRadius: "12px", border: "1px solid #e0e0e0", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
        <Box>
          <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="#453c72" sx={{ mb: 0.5 }}>
            Overview Dashboard
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage your CRM statistics and usage limits.
          </Typography>
        </Box>
        <Box sx={{ mt: isMobile ? 2 : 0, textAlign: isMobile ? "left" : "right", borderLeft: isMobile ? "none" : "2px solid #f0f0f0", pl: isMobile ? 0 : 3 }}>
          <Typography variant="body2" color="textSecondary" fontWeight="600" sx={{ textTransform: "uppercase" }}>
            Current Plan
          </Typography>
          <Typography variant="h6" fontWeight="bold" color="#453c72">
            {stats.planName.toUpperCase()}
          </Typography>
          {stats.planName.toLowerCase() !== 'free' && (
            <Typography variant="caption" color="error" fontWeight="600">
              Expires: {formatDate(stats.planExpiry)}
            </Typography>
          )}
        </Box>
      </Box>

      {/* CRM Lifetime Stats */}
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: "#607d8b", textTransform: "uppercase", letterSpacing: 1 }}>Overall CRM Statistics</Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <MetricCard title="Total Cards Created" value={stats.totalCards} icon={<FaChartPie size={24} />} color="#1976d2" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <MetricCard title="Total Emails Sent" value={stats.totalEmails} icon={<PiDatabaseFill size={24} />} color="#9c27b0" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <MetricCard title="Active Plan" value={stats.planName.toUpperCase()} icon={<FaRegClock size={24} />} color="#2e7d32" />
        </Grid>
      </Grid>

      {/* Split Usage & Balance */}
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: "#607d8b", textTransform: "uppercase", letterSpacing: 1 }}>Card Quota (Daily)</Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6}>
          <MetricCard title="Cards Used Today" value={`${stats.cardsUsed} / ${stats.cardsLimit === 999999 ? '∞' : stats.cardsLimit}`} icon={<FaFileInvoice size={24} />} color="#f57c00" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MetricCard title="Cards Balance (Remaining)" value={getCardsBalance()} icon={<FaFileInvoice size={24} />} color="#43a047" />
        </Grid>
      </Grid>

      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: "#607d8b", textTransform: "uppercase", letterSpacing: 1 }}>Email Quota (Daily)</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <MetricCard title="Emails Sent Today" value={`${stats.emailsUsed} / ${stats.emailsLimit === 999999 ? '∞' : stats.emailsLimit}`} icon={<MdAttachEmail size={24} />} color="#d32f2f" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MetricCard title="Emails Balance (Remaining)" value={getEmailsBalance()} icon={<MdAttachEmail size={24} />} color="#00897b" />
        </Grid>
      </Grid>

      <Box sx={{ mt: 5, p: 4, borderRadius: "16px", background: "linear-gradient(135deg, #453c72 0%, #2a2444 100%)", color: "white", textAlign: "center", boxShadow: "0 10px 30px rgba(69, 60, 114, 0.3)" }}>
        <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
          Ready to create your next campaign?
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
          Head over to the Template section to start building dynamic, personalized cards and emails for your clients.
        </Typography>
      </Box>
    </Box>
  );
};

export default UserDashboard;
